import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Key, Plus, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoggerWebhookManager({ open, onClose }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ uid: "", name: "", site: "", shipment_id: "" });
  const [revealed, setRevealed] = useState(null);

  const load = useCallback(async () => {
    try { setItems((await axios.get(`${API}/logger-webhooks/mappings`)).data.items || []); }
    catch { toast.error("Unable to load inbound logger integrations"); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);
  if (!open) return null;

  const copy = async (value) => {
    await navigator.clipboard.writeText(value);
    toast.success("Copied to clipboard");
  };
  const create = async (event) => {
    event.preventDefault();
    try {
      const { data } = await axios.post(`${API}/logger-webhooks/mappings`, form);
      setRevealed({ uid: data.uid, secret: data.secret });
      setForm({ uid: "", name: "", site: "", shipment_id: "" });
      load();
    } catch (error) { toast.error(error?.response?.data?.detail || "Unable to create logger integration"); }
  };
  const rotate = async (row) => {
    if (!window.confirm(`Rotate the provider credential for ${row.name}? The old credential will stop working immediately.`)) return;
    try {
      const { data } = await axios.post(`${API}/logger-webhooks/mappings/${row.id}/rotate-secret`);
      setRevealed({ uid: data.uid, secret: data.secret });
      load();
    } catch (error) { toast.error(error?.response?.data?.detail || "Credential rotation failed"); }
  };

  return <div className="fixed inset-0 z-[2100] bg-black/70 flex items-center justify-center p-4">
    <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-lg border border-zinc-700 bg-zinc-950 text-zinc-100 p-6">
      <div className="flex justify-between items-start gap-4"><div><h2 className="text-xl font-semibold">Inbound Logger Webhooks</h2><p className="text-sm text-zinc-400 mt-1">Create or rotate credentials for provider-to-ColdChain logger reports.</p></div><Button variant="ghost" onClick={onClose}><X size={20} /></Button></div>
      {revealed && <div className="mt-5 rounded border border-amber-700 bg-amber-950/40 p-4"><p className="font-medium text-amber-200">Copy this credential now — it will not be shown again.</p><p className="mt-2 text-sm text-zinc-300">Provider header: <code>X-Logger-Secret</code></p><div className="mt-2 flex gap-2"><Input readOnly value={revealed.secret} className="font-mono" /><Button onClick={() => copy(revealed.secret)}><Copy className="mr-1" size={16} />Copy</Button></div><p className="mt-2 text-xs text-zinc-400">Logger UID: {revealed.uid}</p></div>}
      <form onSubmit={create} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 rounded border border-zinc-800 p-4"><Input required placeholder="Logger UID" value={form.uid} onChange={e => setForm({ ...form, uid: e.target.value })} /><Input required placeholder="Friendly logger name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /><Input placeholder="Site (optional)" value={form.site} onChange={e => setForm({ ...form, site: e.target.value })} /><Input placeholder="Shipment ID (optional)" value={form.shipment_id} onChange={e => setForm({ ...form, shipment_id: e.target.value })} /><div className="md:col-span-2"><Button type="submit"><Plus className="mr-1" size={16} />Add logger webhook</Button></div></form>
      <div className="mt-6 space-y-3">{items.map(row => <div key={row.id} className="rounded border border-zinc-800 p-4 flex flex-col gap-3 md:flex-row md:justify-between"><div><p className="font-medium">{row.name}</p><p className="font-mono text-sm text-zinc-400">{row.webhook_uid}</p><p className="mt-1 text-xs text-zinc-500">Header: X-Logger-Secret · Endpoint: {row.endpoint}</p></div><Button variant="outline" className="border-zinc-700 self-start" onClick={() => rotate(row)}><Key className="mr-1" size={16} />Rotate credential</Button></div>)}</div>
    </div>
  </div>;
}
